import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  UserPlus, ShieldCheck, Mail, Phone, Trash2, 
  Search, Lock, CheckCircle, X, ShieldAlert, Key,
  TrendingUp, Wallet, Receipt, Calendar, AlertCircle, Plus, Info,
  ChevronLeft, ChevronRight
} from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';
import styles from "../styles/AdminStaff.module.css";

export default function AdminStaff() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("staff");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [newRevenue, setNewRevenue] = useState({ title: "", amount: "", source: "Manager" });

  const [showResetModal, setShowResetModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [resetTarget, setResetTarget] = useState({ id: "", name: "" });
  const [passwordForm, setPasswordForm] = useState({ newPassword: "", confirmPassword: "" });
  const [showError, setShowError] = useState("");

  // Financial States
  const [expenses, setExpenses] = useState([]);
  const [upcomingBills, setUpcomingBills] = useState([]); 
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [newExpense, setNewExpense] = useState({ title: "", amount: "" });
  const [newBill, setNewBill] = useState({ title: "", amount: "", due_date: "" });

  // Pagination States
  const [expensePage, setExpensePage] = useState(1);
  const [billPage, setBillPage] = useState(1);
  const itemsPerPage = 5;

  // Form State for Add Admin
  const [newAdmin, setNewAdmin] = useState({
    name: "", email: "", phone: "", role: "manager", password: "", username: "", salary: ""
  });


  const handleOpenReset = (id, name) => {
  setShowError("");
  setResetTarget({ id, name });
  setPasswordForm({ newPassword: "", confirmPassword: "" });
  setShowResetModal(true);
};

const submitUserPasswordReset = async (e) => { 
  e.preventDefault();
  
  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
     setShowError("Passwords do not match");
    return;
  }
  if (passwordForm.newPassword.length < 6) {
    setShowError("Password must be at least 6 characters");
    return;
  }

  try {
    await axios.put(`/api/admin/system-staff/reset-password/${resetTarget.id}`, { 
      password: passwordForm.newPassword 
    });
    toast.success(`Password for ${resetTarget.name} updated!`);
    setShowResetModal(false);
    setShowError("");
  } catch (err) {
    toast.error("Failed to reset password");
  }
};

const handleAdminSelfReset = async (e) => {
  e.preventDefault();
    
  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
       setShowError("Passwords do not match");
    return;
  }
  if (passwordForm.newPassword.length < 6) {
     setShowError("Password must be at least 6 characters");
    return;
  } 

  try {
    // Note: Ensure you have this endpoint on your backend
    await axios.put('/api/admin/update-own-password', { 
      password: passwordForm.newPassword 
    });
    toast.success("Your password has been updated!");
    setShowProfileModal(false);
    setShowError("");
  } catch (err) {
    toast.error("Failed to update your password");
  }
};


  const handleAddRevenue = async () => {
  if (!newRevenue.title || !newRevenue.amount) return;
  try {
    // This hits your existing revenue logic or a new endpoint
    await axios.post('/api/admin/add-direct-revenue', newRevenue);
    setNewRevenue({ title: "", amount: "", source: "Manager" });
    fetchData(); // Refresh the monthlyRevenue h3 at the top
  
    toast.success("Revenue recorded successfully!");
  } catch (err) { 
    toast.error("Failed to record revenue. Please try again.");
  }
};


  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const staffRes = await axios.get('/api/admin/system-staff');
      setAdminList(staffRes.data || []);

      const finRes = await axios.get('/api/admin/financial-summary');
      setExpenses(finRes.data.expenses || []);
      setUpcomingBills(finRes.data.upcomingBills || []); 
      setMonthlyRevenue(finRes.data.totalRevenue || 0);
    } catch (err) {
      console.error("Data fetch failed");
      toast.error("Failed to load data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- PAGINATION LOGIC ---
  const paginate = (data, currentPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const renderPagination = (data, currentPage, setPage) => {
    const totalPages = Math.ceil(data.length / itemsPerPage);
    if (totalPages <= 1) return null;

    return (
      <div className={styles["pagination-controls"]}>
        <button 
          disabled={currentPage === 1} 
          onClick={() => setPage(prev => prev - 1)}
          className={styles["page-btn"]}
        >
          <ChevronLeft size={16} />
        </button>
        <span className={styles["page-info"]}>Page {currentPage} of {totalPages}</span>
        <button 
          disabled={currentPage === totalPages} 
          onClick={() => setPage(prev => prev + 1)}
          className={styles["page-btn"]}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  // --- ACTIONS ---
  const handleAddExpense = async () => {
    if (!newExpense.title || !newExpense.amount) return;
    try {
      await axios.post('/api/admin/add-expense', newExpense);
      setNewExpense({ title: "", amount: "" });
      setExpensePage(1); // Reset to first page to see new entry
      fetchData();
    } catch (err) {
      // alert("Error adding expense");
      toast.error("Failed to add expense. Please try again.");
     }
  };

  const handleAddBill = async () => {
    if (!newBill.title || !newBill.amount || !newBill.due_date) return;
    try {
      await axios.post('/api/admin/add-bill', newBill);
      setNewBill({ title: "", amount: "", due_date: "" });
      setBillPage(1); // Reset to first page
      fetchData();
      toast.success("Bill scheduled successfully!");
    } catch (err) {
      toast.error("Failed to schedule bill. Please try again.");
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/admin/create-system-staff', newAdmin);
      setShowAddModal(false);
      setNewAdmin({ name: "", email: "", phone: "", role: "manager", password: "", username: "", salary: "" });
      fetchData();
      toast.success("New account created successfully!");
    } catch (err) {
      toast.error("Failed to create account. Please try again.");
    }
  };

  const handleResetPassword = async (id, name) => {
    const newPassword = window.prompt(`Enter a new password for ${name}:`);
    if (!newPassword || newPassword.length < 6) return toast.error("Invalid password. Must be at least 6 characters.");
    try {
      await axios.put(`/api/admin/system-staff/reset-password/${id}`, { password: newPassword });
      toast.success("Password updated!");
    } catch (err) { toast.error("Failed to reset password"); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Revoke access for ${name}?`)) return;
    try {
      await axios.delete(`/api/admin/system-staff/${id}`);
      setAdminList(prev => prev.filter(a => a.id !== id));
      toast.success("Account deleted successfully!");
    } catch (err) {
      toast.error("Failed to delete account. Please try again.");
    }
  };

const handlePayBill = async (id, title) => {
  if (!window.confirm(`Mark "${title}" as paid? This will add it to your monthly expenses.`)) return;
  try {
    await axios.put(`/api/admin/pay-bill/${id}`);
    // This is good, it triggers your fetchData which refreshes the summary
    fetchData(); 
    toast.success("Payment recorded and outflow updated!");
  } catch (err) {
    toast.error("Failed to process payment. Please try again.");
  }
};

// Force Number conversion and handle empty arrays safely
const totalExpenses = Array.isArray(expenses) 
    ? expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0) 
    : 0;

const totalSalaries = Array.isArray(adminList) 
    ? adminList.reduce((sum, admin) => sum + (parseFloat(admin.salary) || 0), 0) 
    : 0;

// netProfit calculation
const netProfit = (parseFloat(monthlyRevenue) || 0) - totalExpenses - totalSalaries;

  if (loading) return <div className={styles.loading}>Loading management workspace...</div>;

  return (
    <div className={styles["admin-portal-wrapper"]}>
        <Toaster position="top-right" toastOptions={{ style: { background: '#111', color: '#fff', border: '1px solid #caa45f' } }} />
    <header className={styles["portal-header"]}>
  <div className={styles["header-main-content"]}>
    <h2 className={styles["title-main"]}>Administration & Finance</h2>
    <div className={styles["tab-switcher"]}>
       <button className={activeTab === "staff" ? styles.activeTab : styles.inactiveTab} onClick={() => setActiveTab("staff")}>Admin Users</button>
       <button className={activeTab === "financials" ? styles.activeTab : styles.inactiveTab} onClick={() => setActiveTab("financials")}>Business Finance</button>
    </div>
  </div>
  <div className={styles["header-actions"]}>
    <button className={styles["security-btn"]} onClick={() =>{ setShowProfileModal(true); setShowError("")}}>
      <ShieldCheck size={18} /> <span>My Security</span>
    </button>
    {activeTab === "staff" && (
      <button className={styles["gold-action-btn"]} onClick={() => setShowAddModal(true)}>
        <UserPlus size={18} /> <span>Add Admin</span>
      </button>
    )}
  </div>
</header>

      {activeTab === "staff" ? (
        <div className={styles["view-container"]}>
          <div className={styles["filter-section"]}>
            <div className={styles["search-box"]}>
              <Search size={18} color="#caa45f" />
              <input type="text" placeholder="Search by name..." onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>

          <div className={styles["staff-grid"]}>
            {adminList.filter(a => a.name?.toLowerCase().includes(searchTerm.toLowerCase())).map((admin) => (
              <div key={admin.id} className={styles["staff-card"]}>
                <div className={styles["card-header"]}>
                  <div className={`${styles["role-badge"]} ${styles[admin.role]}`}>{admin.role?.toUpperCase()}</div>
                  <button className={styles["delete-btn"]} onClick={() => handleDelete(admin.id, admin.name)}><Trash2 size={16} /></button>
                </div>
                <div className={styles["card-body"]}>
                  <div className={styles["avatar-large"]}>{admin.name ? admin.name[0] : "?"}</div>
                  <h3 className={styles["staff-name"]}>{admin.name}</h3>
                  <div className={styles["finance-pill"]}>
                   <Wallet size={14} /> ₦{Number(admin.salary || 0).toLocaleString()} /mo
                  </div>
                </div>
                <div className={styles["card-footer"]}>
                  <button className={styles["edit-btn"]} onClick={() => handleOpenReset(admin.id, admin.name)}>
                    <Key size={14} /> Reset Password
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={styles["financial-dashboard"]}>
          <div className={styles["finance-stats-row"]}>
            <div className={styles["f-stat-card"]}>
                <div className={styles["stat-icon-wrap"]} style={{background: '#f3ead8'}}><TrendingUp color="#9a6a1c" /></div>
                <div className={styles["stat-text-box"]}><p style={{color:"#b2b6b4"}}>Recorded Revenue</p><h3 style={{color:"#1f1b16"}}>₦{monthlyRevenue.toLocaleString()}</h3></div>
            </div>
            <div className={styles["f-stat-card"]}>
                <div className={styles["stat-icon-wrap"]} style={{background: '#ff4d4d20'}}><Receipt color="#ff4d4d" /></div>
                <div className={styles["stat-text-box"]}><p style={{color:"#b6b2b2"}}>Total Outflow</p><h3 style={{color:"#d40a0a"}}>₦{(totalExpenses + totalSalaries).toLocaleString()}</h3></div>
            </div>
            <div className={styles["f-stat-card"]}>
                <div className={styles["stat-icon-wrap"]} style={{background: '#caa45f20'}}><Wallet color="#caa45f" /></div>
                <div className={styles["stat-text-box"]}><p style={{color:"#b6b2b2"}}>Net Profit</p><h3 style={{color: '#caa45f'}}>₦{netProfit.toLocaleString()}</h3></div>
            </div>
          </div>

          <div className={styles["finance-layout-grid"]}>
            <div className={styles["finance-col-main"]}>
               <div className={styles["content-card"]}>
                  <div className={styles["card-head"]}><Receipt size={18} /> <h3>Expense History</h3></div>
                  <div className={styles["log-scroll-area"]}>
                    {expenses.length > 0 ? paginate(expenses, expensePage).map(exp => (
                      <div key={exp.id} className={styles["log-entry"]}>
                        <span>{exp.title}</span>
                        
                        <span className={styles.redText}>-₦{Number(exp.amount).toLocaleString()}</span>
                        <span className={styles["log-date"]}>{new Date(exp.created_at).toLocaleDateString()}</span>

                      </div>
                    )) : <p className={styles.emptyHint}>No extra expenses recorded.</p>}
                    
                    {/* PAGINATION FOR EXPENSES */}
                    {renderPagination(expenses, expensePage, setExpensePage)}

                    <div className={styles["log-entry"]} style={{borderTop: '1px solid #222', marginTop: '10px'}}>
                        <span>System Staff Salaries</span>
                        <span className={styles.redText}>-₦{totalSalaries.toLocaleString()}</span>
                    </div>
                  </div>
               </div>

               <div className={styles["content-card"]}>
                  <div className={styles["card-head"]}><AlertCircle size={18} color="#ffb100" /> <h3>Upcoming Bills</h3></div>
                  <div className={styles["log-scroll-area"]}>
                   {upcomingBills.length > 0 ? paginate(upcomingBills, billPage).map(bill => (
  <div key={bill.id} className={styles["bill-entry"]}>
    <div className={styles["bill-meta"]}>
       <strong>{bill.title}</strong>
       <small>Due: {new Date(bill.due_date).toLocaleDateString()}</small>
    </div>
    <div className={styles["bill-action-wrap"]}>
        <span className={styles.billVal}>₦{Number(bill.amount).toLocaleString()}</span>
        <button 
          className={styles["pay-bill-btn"]} 
          onClick={() => handlePayBill(bill.id, bill.title)}
        >
          Pay Now
        </button>
    </div>
  </div>
)) : (
  <div className={styles.emptyMsg}><Info size={20} /><p>No bills due this week</p></div>
)}
                    {/* PAGINATION FOR BILLS */}
                    {renderPagination(upcomingBills, billPage, setBillPage)}
                  </div>
               </div>
            </div>



            <div className={styles["finance-col-side"]}>

              <div className={styles["action-panel"]} style={{marginBottom: '20px'}}>
      <h3 style={{color: '#9a6a1c'}}>Record Revenue</h3>
      <p>Record revenue received directly by the salon.</p>
      <div className={styles["panel-form"]}>
        <div className={styles["input-group-premium"]}>
            <label>Service / Source</label>
            <input 
              type="text" 
              placeholder="Manager Pedicure, Walk-in Product..." 
              value={newRevenue.title} 
              onChange={e => setNewRevenue({...newRevenue, title: e.target.value})} 
            />
        </div>
        <div className={styles["input-group-premium"]}>
            <label>Staff Category</label>
            <select 
              value={newRevenue.source} 
              className={styles.premiumSelect}
              onChange={e => setNewRevenue({...newRevenue, source: e.target.value})}
            >
                <option value="Manager">Shop Manager</option>
                <option value="Receptionist">Receptionist</option>
                <option value="Walk-in">Direct Walk-in</option>
            </select>
        </div>
        <div className={styles["input-group-premium"]}>
            <label>Amount (100%)</label>
            <div className={styles["currency-input"]}>
                <span>₦</span>
                <input 
                  type="number" 
                  value={newRevenue.amount} 
                  onChange={e => setNewRevenue({...newRevenue, amount: e.target.value})} 
                />
            </div>
        </div>
        <button 
          onClick={handleAddRevenue} 
          className={styles["save-btn"]} 
          
        >
          <TrendingUp size={18} /> Record Income
        </button>
      </div>
   </div>

               <div className={styles["action-panel"]}>
                  <h3>Record Expense</h3>
                  <div className={styles["panel-form"]}>
                    <div className={styles["input-group-premium"]}>
                        <label>Title</label>
                        <input type="text" placeholder="Fuel, Water..." value={newExpense.title} onChange={e => setNewExpense({...newExpense, title: e.target.value})} />
                    </div>
                    <div className={styles["input-group-premium"]}>
                        <label>Amount</label>
                        <div className={styles["currency-input"]}>
                            <span>₦</span>
                            <input type="number" value={newExpense.amount} onChange={e => setNewExpense({...newExpense, amount: e.target.value})} />
                        </div>
                    </div>
                    <button onClick={handleAddExpense} className={styles["save-btn"]}><Plus size={18} /> Record</button>
                  </div>
               </div>

               <div className={styles["action-panel"]} style={{marginTop: '20px'}}>
                  <h3 style={{color: '#ffb100'}}>Schedule Bill</h3>
                  <div className={styles["panel-form"]}>
                    <div className={styles["input-group-premium"]}><label>Bill Name</label><input type="text" placeholder="Shop Rent..." value={newBill.title} onChange={e => setNewBill({...newBill, title: e.target.value})} /></div>
                    <div className={styles["input-group-premium"]}><label>Amount</label><div className={styles["currency-input"]}><span>₦</span><input type="number" value={newBill.amount} onChange={e => setNewBill({...newBill, amount: e.target.value})} /></div></div>
                    <div className={styles["input-group-premium"]}><label>Due Date</label><input type="date" className={styles.dateInput} value={newBill.due_date} onChange={e => setNewBill({...newBill, due_date: e.target.value})} /></div>
                    <button onClick={handleAddBill} className={styles["save-btn"]} style={{background: '#ffb100'}}><Calendar size={18} /> Schedule</button>
                  </div>
               </div>
            </div>
          </div>
        </div>
      )}

      {/* --- ADD ADMIN MODAL --- */}
      {showAddModal && (
        <div className={styles["modal-overlay"]}>
          <div className={styles["modal-container"]}>
            <div className={styles["modal-header"]}>
               <div className={styles["icon-box"]}><ShieldAlert size={20} color="#caa45f" /></div>
               <h3>Add Admin User</h3>
               <button className={styles["close-btn"]} onClick={() => setShowAddModal(false)}>
                   <X size={20} />
               </button>
            </div>

            <form onSubmit={handleCreateAccount} className={styles["admin-form"]}>
              <div className={styles["input-group"]}>
                <label>Full Name</label>
                <input type="text" required value={newAdmin.name} onChange={e => setNewAdmin({...newAdmin, name: e.target.value})} />
              </div>

              <div className={styles["form-row"]}>
                <div className={styles["input-group"]}>
                  <label>Username</label>
                  <input type="text" required value={newAdmin.username} onChange={e => setNewAdmin({...newAdmin, username: e.target.value})} />
                </div>
                <div className={styles["input-group"]}>
                  <label>Monthly Salary (₦)</label>
                  <input type="number" required value={newAdmin.salary} onChange={e => setNewAdmin({...newAdmin, salary: e.target.value})} />
                </div>
              </div>

              <div className={styles["input-group"]}>
                <label>Assign Role</label>
                <select value={newAdmin.role} onChange={e => setNewAdmin({...newAdmin, role: e.target.value})}>
                  <option value="manager">Shop Manager</option>
                  <option value="cashier">Cashier</option>
                </select>
              </div>

              <div className={styles["input-group"]}>
                <label>Email Address</label>
                <input type="email" required value={newAdmin.email} onChange={e => setNewAdmin({...newAdmin, email: e.target.value})} />
              </div>

              <div className={styles["input-group"]}>
                <label>Initial Password</label>
                <div className={styles["password-input-wrap"]}>
                  <Lock size={16} />
                  <input type="password" required value={newAdmin.password} onChange={e => setNewAdmin({...newAdmin, password: e.target.value})} />
                </div>
              </div>

              <div className={styles["modal-footer"]}>
                <button type="button" className={styles["cancel-btn"]} onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className={styles["submit-btn"]}>Create Admin Account</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- USER PASSWORD RESET MODAL --- */}
{showResetModal && (
  <div className={styles["modal-overlay"]}>
    <div className={styles["modal-container"]}>
      <div className={styles["modal-header"]}>
         <div className={styles["icon-box"]}><Key size={20} color="#caa45f" /></div>
         <h3>Reset Password: {resetTarget.name}</h3>
         <button className={styles["close-btn"]} onClick={() => setShowResetModal(false)}><X size={20} /></button>
      </div>
        { showError && <div className={styles["error-message-glass"]}>{showError}</div> }
      <form onSubmit={submitUserPasswordReset} className={styles["admin-form"]}>
        <div className={styles["input-group"]}>
          <label>New Password</label>
          <input type="password" required value={passwordForm.newPassword} onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})} />
        </div>
        <div className={styles["input-group"]}>
          <label>Confirm Password</label>
          <input type="password" required value={passwordForm.confirmPassword} onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} />
        </div>
        <div className={styles["modal-footer"]}>
          <button type="button" className={styles["cancel-btn"]} onClick={() => setShowResetModal(false)}>Cancel</button>
          <button type="submit" className={styles["submit-btn"]}>Update User Password</button>
        </div>
      </form>
    </div>
  </div>
)}

{/* --- SUPER ADMIN SELF-RESET MODAL --- */}
{showProfileModal && (
  <div className={styles["modal-overlay"]}>
    <div className={styles["modal-container"]} style={{border: '1px solid #caa45f'}}>
      <div className={styles["modal-header"]}>
         <div className={styles["icon-box"]} style={{background: '#caa45f20'}}><ShieldCheck size={20} color="#caa45f" /></div>
         <h3>My Security Settings</h3>
         <button className={styles["close-btn"]} onClick={() => setShowProfileModal(false)}><X size={20} /></button>
      </div>
      <form onSubmit={handleAdminSelfReset} className={styles["admin-form"]}>
          { showError && <div className={styles["error-message-glass"]}>{showError}</div> }
        <p style={{fontSize: '13px', color: '#888', marginBottom: '15px'}}>Update your Super Admin credentials. Choose a strong password.</p>
        <div className={styles["input-group"]}>
          <label>Add Admin Password</label>
          <input type="password" required value={passwordForm.newPassword} onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})} />
        </div>
        <div className={styles["input-group"]}>
          <label>Confirm New Password</label>
          <input type="password" required value={passwordForm.confirmPassword} onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} />
        </div>
        <div className={styles["modal-footer"]}>
          <button type="button" className={styles["cancel-btn"]} onClick={() => setShowProfileModal(false)}>Keep Current</button>
          <button type="submit" className={styles["submit-btn"]} style={{background: '#caa45f'}}>Apply My New Password</button>
        </div>
      </form>
    </div>
  </div>
)}
    </div>
  );
}